import React, { useState } from "react";
import { LogOut, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../context/AdminAuth";

/**
 * Page header.
 *
 * Three things went from this version: its own `getUser()` + profile query
 * (AdminAuthProvider owns that now), a search box that was never wired to
 * anything, and a notification bell with a permanent unread dot behind it.
 * Controls that don't do anything teach people to stop trusting the ones that
 * do.
 *
 * What replaced them is the one control a panel like this actually needs on
 * every screen: a way out.
 */
export default function Topbar({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { identity, signOut } = useAdminAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const initial = identity?.fullName?.[0]?.toUpperCase() ?? "A";

  return (
    <header
      style={{
        minHeight: "var(--topbar-height)",
        background: "var(--topbar-bg)",
        borderBottom: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "12px 28px",
        position: "sticky",
        top: 0,
        zIndex: 50,
        boxShadow: "0 1px 8px rgba(0,0,0,0.04)",
        backdropFilter: "blur(18px)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <h1
          style={{
            fontSize: 20,
            fontWeight: 850,
            color: "var(--text-primary)",
            letterSpacing: "-0.04em",
            margin: 0,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <div style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 3 }}>
            {subtitle}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {actions}

        <div style={{ position: "relative" }}>
          <button
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              background: "#fff",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: "6px 10px 6px 6px",
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--primary), #8b7ffd)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {initial}
            </span>
            <span style={{ textAlign: "left", lineHeight: 1.25 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 12.5,
                  fontWeight: 750,
                  color: "var(--text-primary)",
                }}
              >
                {identity?.fullName ?? "Admin"}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 10.5,
                  color: "var(--text-muted)",
                  maxWidth: 150,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {identity?.email}
              </span>
            </span>
          </button>

          {menuOpen && (
            <>
              {/* Click-away layer. Without it the menu only closes by toggling
                  the same button, which nobody expects. */}
              <div
                onClick={() => setMenuOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 60 }}
              />
              <div
                role="menu"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  background: "#fff",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                  boxShadow: "var(--shadow-md)",
                  minWidth: 210,
                  padding: 6,
                  zIndex: 61,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 11px",
                    fontSize: 11.5,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  <ShieldCheck size={13} color="var(--success)" />
                  Signed in as admin
                </div>
                <button
                  role="menuitem"
                  onClick={async () => {
                    await signOut();
                    navigate("/login", { replace: true });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    width: "100%",
                    padding: "10px 11px",
                    border: "none",
                    background: "none",
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--danger)",
                    textAlign: "left",
                  }}
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
